import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  getBooking, respondToBooking, signBooking, contractUrl,
  AGREEMENT_LABEL, type BookingDetail,
} from '../../lib/artist';
import { ApiError } from '../../lib/apiClient';
import { SignaturePad } from '../../components/SignaturePad';
import { formatMoney, formatDate } from '../../lib/format';
import './ArtistPages.css';

/**
 * Review and answer one booking request.
 *
 * Two engine facts shape this screen, both verified rather than assumed:
 *
 *   ACCEPTING IS NOT A STATE CHANGE. PUT /bookings/:id/respond with
 *   action='accept' leaves the agreement 'pending' and writes an audit entry —
 *   signatures are what move it to 'signed'. A two-button "accept / decline"
 *   screen would therefore lie: pressing accept and walking away leaves the
 *   booking exactly where it was. So accept and sign are one flow, and the
 *   accept call is made as the first half of signing rather than on its own.
 *
 *   THERE IS NO 'DECLINED' STATUS. Declining sets 'cancelled' — the same value
 *   an organizer cancellation produces. The confirmation says so plainly
 *   instead of implying a distinct, softer outcome.
 */

export function BookingReview() {
  const { agreementId = '' } = useParams();
  const navigate = useNavigate();

  const [b, setB] = useState<BookingDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<'idle' | 'sign' | 'decline'>('idle');
  const [signature, setSignature] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    getBooking(agreementId)
      .then((r) => { setB(r); setError(null); })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load this booking.'))
      .finally(() => setLoading(false));
  }, [agreementId]);

  useEffect(load, [load]);

  async function acceptAndSign() {
    if (!b || !signature) return;
    setBusy(true);
    setError(null);
    try {
      // Record the acceptance first so the audit trail shows the artist agreed
      // before their signature was captured, then sign.
      if (b.canRespond) await respondToBooking(b.agreementId, 'accept');
      const r = await signBooking(b.agreementId, signature);
      setFlash(r.status === 'signed'
        ? 'Signed. Both parties have now signed, the contract PDF is available below.'
        : 'Signed. The agreement is confirmed once the organizer countersigns.');
      setMode('idle');
      setSignature(null);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign the agreement.');
    } finally {
      setBusy(false);
    }
  }

  async function decline() {
    if (!b) return;
    setBusy(true);
    setError(null);
    try {
      await respondToBooking(b.agreementId, 'decline');
      navigate('/artist/bookings', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not decline the request.');
      setBusy(false);
    }
  }

  if (loading) return <p className="art-status">Loading…</p>;
  if (error && !b) return <p className="art-status art-error" role="alert">{error}</p>;
  if (!b) return null;

  return (
    <div className="art-page art-page--narrow">
      <nav className="art-breadcrumb">
        <Link to="/artist/bookings">← All bookings</Link>
      </nav>

      <header className="art-header">
        <div>
          <h1 className="art-title">{b.event.title}</h1>
          <p className="art-lede">
            {b.event.date ? formatDate(b.event.date) : 'Date TBC'}
            {b.event.venue ? ` · ${b.event.venue}` : ''}
          </p>
          <p className="art-hint mono">{b.contractNumber}</p>
        </div>
        <span className={`art-badge art-badge--${b.status}`}>{AGREEMENT_LABEL[b.status]}</span>
      </header>

      {error && <p className="art-error" role="alert">{error}</p>}
      {flash && <p className="art-flash" role="status">{flash}</p>}

      {b.routedAgency && (
        <p className="art-flash" role="status">
          <span className="art-badge art-badge--agency">Agency</span>{' '}
          This request was also routed to <strong>{b.routedAgency.agencyName}</strong>, who
          can answer it on your behalf. You can still answer it yourself, whoever responds
          first settles it.
        </p>
      )}

      <section className="art-card">
        <h2 className="art-section__title">The offer</h2>
        <dl className="art-facts">
          <div><dt>Performance fee</dt><dd className="art-money">{formatMoney(b.performanceFeeCents)}</dd></div>
          <div><dt>Deposit</dt><dd className="mono">{formatMoney(b.depositCents)}</dd></div>
          <div><dt>Balance on performance</dt><dd className="mono">{formatMoney(b.balanceCents)}</dd></div>
          <div>
            <dt>Cancellation penalty</dt>
            <dd className="mono">
              {b.cancellationPenaltyCents == null ? ' | ' : formatMoney(b.cancellationPenaltyCents)}
            </dd>
          </div>
          <div><dt>Set time</dt><dd className="mono">{b.performanceStartTime ?? ' | '}</dd></div>
          <div>
            <dt>Set length</dt>
            <dd className="mono">{b.setDurationMinutes ? `${b.setDurationMinutes} min` : ' | '}</dd>
          </div>
          <div><dt>Organizer</dt><dd>{b.organizer.name ?? ' | '}</dd></div>
          <div><dt>Contact</dt><dd className="mono art-hint">{b.organizer.email}</dd></div>
        </dl>

        <div>
          <dt className="art-hint">Contract terms</dt>
          {b.contractTerms
            ? <p className="art-terms">{b.contractTerms}</p>
            : <p className="art-hint art-muted">No additional terms were attached.</p>}
        </div>

        <dl className="art-facts">
          <div>
            <dt>Organizer signed</dt>
            <dd>{b.organizerSignedAt ? formatDate(b.organizerSignedAt) : <span className="art-muted">Not yet</span>}</dd>
          </div>
          <div>
            <dt>You signed</dt>
            <dd>{b.artistSignedAt ? formatDate(b.artistSignedAt) : <span className="art-muted">Not yet</span>}</dd>
          </div>
        </dl>
      </section>

      {mode === 'sign' && (
        <section className="art-prompt">
          <h3 className="art-prompt__title">Accept and sign</h3>
          <div className="art-prompt__body">
            <ul className="art-consequences">
              <li>You commit to performing for <strong>{formatMoney(b.performanceFeeCents)}</strong>.</li>
              <li>
                The deposit of <strong>{formatMoney(b.depositCents)}</strong> is released once
                both parties have signed; the balance after attendance is verified at the door.
              </li>
              {b.cancellationPenaltyCents != null && b.cancellationPenaltyCents > 0 && (
                <li>
                  Cancelling after signing carries a penalty of{' '}
                  <strong>{formatMoney(b.cancellationPenaltyCents)}</strong>.
                </li>
              )}
              <li>The agreement is confirmed only once the organizer countersigns.</li>
            </ul>
          </div>

          <SignaturePad onChange={setSignature} disabled={busy} label="Sign here" />

          <div className="art-prompt__actions">
            <button type="button" className="button button--ghost" disabled={busy}
              onClick={() => { setMode('idle'); setSignature(null); }}>
              Cancel
            </button>
            <button type="button" className="button button--primary"
              disabled={busy || !signature} onClick={acceptAndSign}>
              {busy ? 'Signing…' : 'Accept & sign'}
            </button>
          </div>
        </section>
      )}

      {mode === 'decline' && (
        <section className="art-prompt">
          <h3 className="art-prompt__title">Decline this request?</h3>
          <div className="art-prompt__body">
            <p>
              The agreement is set to <strong>cancelled</strong>. Voleto has no separate
              “declined” state, so this reads the same as a cancellation to the organizer.
            </p>
            <p>It cannot be reopened, the organizer would have to send a new request.</p>
          </div>
          <div className="art-prompt__actions">
            <button type="button" className="button button--ghost" disabled={busy}
              onClick={() => setMode('idle')}>
              Keep it
            </button>
            <button type="button" className="button button--ghost art-danger" disabled={busy}
              onClick={decline}>
              {busy ? 'Declining…' : 'Decline request'}
            </button>
          </div>
        </section>
      )}

      {mode === 'idle' && (
        <div className="art-actions">
          {b.canSign && (
            <button type="button" className="button button--primary" onClick={() => setMode('sign')}>
              Accept &amp; sign
            </button>
          )}
          {b.canRespond && (
            <button type="button" className="button button--ghost art-danger"
              onClick={() => setMode('decline')}>
              Decline
            </button>
          )}
          {b.contractAvailable && (
            <a className="button button--secondary" href={contractUrl(b.agreementId)}>
              Download contract PDF
            </a>
          )}
          {!b.canRespond && !b.canSign && !b.contractAvailable && (
            <p className="art-hint">
              Nothing to do here, this agreement is {AGREEMENT_LABEL[b.status].toLowerCase()}.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
